import { useEffect, useMemo, useRef, useState } from "react";
import { FaCheck, FaRegCalendarAlt } from "react-icons/fa";
import "./BookMeetingModal.css";

const INITIAL_FORM = {
  city: "",
  date: "",
  time: "",
  name: "",
  phone: "",
  email: "",
};

const TIME_SLOTS = [
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "01:00 PM",
  "02:00 PM",
  "03:00 PM",
  "04:00 PM",
  "05:00 PM",
];

const CITIES = ["Pune", "Nashik", "Latur", "Sambhajinagar"];
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  "https://api.dwelloradesigns.com"
).replace(/\/$/, "");

function getTodayValue() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isPastSlot(dateValue, slot) {
  if (!dateValue || dateValue !== getTodayValue()) return false;

  const [hourText, minuteText] = slot.split(" ")[0].split(":");
  const period = slot.split(" ")[1];
  let hour = Number(hourText);

  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;

  const slotDate = new Date(dateValue);
  slotDate.setHours(hour, Number(minuteText), 0, 0);

  return slotDate <= new Date();
}

function openWhatsAppBookingChat({ name, phone, city, date, time, bookingId }) {
  const message = `Hello ${name},

Your meeting has been successfully booked with Dwellora Designs.

City: ${city}
Date: ${date}
Time: ${time}
Booking ID: ${bookingId}

Our team will contact you shortly.

Regards,
Dwellora Designs`;

  const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, "_blank", "noopener,noreferrer");
}

export default function BookMeetingModal({ isOpen, closeModal }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const dateInputRef = useRef(null);

  const availableSlots = useMemo(
    () =>
      TIME_SLOTS.map((slot) => ({
        label: slot,
        disabled: isPastSlot(form.date, slot) || bookedSlots.includes(slot),
      })),
    [bookedSlots, form.date]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") handleClose();
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("meeting-modal-open");
    dialogRef.current?.querySelector("select, input, button")?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("meeting-modal-open");
    };
    // Only run when the modal opens/closes, not on every re-render —
    // otherwise focus gets stolen back to the first field on every keystroke.
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !form.date) {
      setBookedSlots([]);
      return undefined;
    }

    const controller = new AbortController();
    setIsLoadingSlots(true);

    fetch(`${API_BASE_URL}/api/meetings/booked-slots?date=${encodeURIComponent(form.date)}`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Unable to load booked slots.");
        }
        setBookedSlots(Array.isArray(payload.bookedSlots) ? payload.bookedSlots : []);
      })
      .catch((fetchError) => {
        if (fetchError.name !== "AbortError") {
          setBookedSlots([]);
          setError(fetchError.message || "Unable to load booked slots.");
        }
      })
      .finally(() => setIsLoadingSlots(false));

    return () => controller.abort();
  }, [form.date, isOpen]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
  };

  const resetForm = () => {
    setStep(1);
    setForm(INITIAL_FORM);
    setError("");
    setSuccess(false);
    setBookedSlots([]);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    closeModal();
  };

  const nextStep = () => {
    if (step === 1 && !form.city) {
      setError("Please select your city.");
      return;
    }

    if (step === 2 && (!form.date || !form.time)) {
      setError("Please select a date and time slot.");
      return;
    }

    if (step === 2 && bookedSlots.includes(form.time)) {
      setError("This time slot is already booked. Please choose another slot.");
      return;
    }

    setError("");
    setStep((current) => current + 1);
  };

  const prevStep = () => {
    setError("");
    setStep((current) => current - 1);
  };

  // Opens the native date picker no matter where inside the date field the
  // user clicks (not just on the calendar icon).
  const openDatePicker = () => {
    const input = dateInputRef.current;
    if (!input) return;

    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
        return;
      } catch (err) {
        // Fall through to focus() below (e.g. showPicker not allowed yet).
      }
    }
    input.focus();
  };

  const confirmBooking = async () => {
    const cleanName = form.name.trim();
    const cleanPhone = form.phone.trim();
    const cleanEmail = form.email.trim();

    if (!cleanName || !cleanPhone || !cleanEmail) {
      setError("Please fill all contact details.");
      return;
    }

    if (!/^[A-Za-z ]{2,}$/.test(cleanName)) {
      setError("Enter a valid name using letters only.");
      return;
    }

    if (!/^[6-9][0-9]{9}$/.test(cleanPhone)) {
      setError("Enter a valid 10-digit Indian mobile number.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/meetings/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          city: form.city,
          date: form.date,
          time: form.time,
          name: cleanName,
          phone: cleanPhone,
          email: cleanEmail,
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || !payload.success) {
        const validationErrors = payload.errors ? Object.values(payload.errors).join(" ") : "";
        throw new Error(validationErrors || payload.message || "Unable to book meeting.");
      }

      openWhatsAppBookingChat({
        name: cleanName,
        phone: cleanPhone,
        city: form.city,
        date: form.date,
        time: form.time,
        bookingId: payload.bookingId,
      });
      setSuccess(true);
    } catch (submitError) {
      setError(submitError.message || "Unable to book meeting. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="meeting-modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div
        className="meeting-modal-box"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="meeting-modal-title"
      >
        <button
          type="button"
          className="meeting-modal-close"
          onClick={handleClose}
          aria-label="Close booking form"
        >
          &#10005;
        </button>

        <section className="meeting-modal-content">
          {success ? (
            <div className="meeting-modal-success" role="status">
              <span>
                <FaCheck aria-hidden="true" />
              </span>
              <h2>Meeting request saved</h2>
              {/* <p>Backend connect hone ke baad ye booking team tak send hogi.</p> */}
              <button type="button" className="meeting-modal-primary" onClick={handleClose}>
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="meeting-modal-progress" aria-label={`Step ${step} of 3`}>
                {[1, 2, 3].map((item) => (
                  <span
                    key={item}
                    className={item <= step ? "is-active" : ""}
                    aria-hidden="true"
                  />
                ))}
              </div>

              {error && <p className="meeting-modal-error">{error}</p>}

              {step === 1 && (
                <div className="meeting-modal-step">
                  <h2 id="meeting-modal-title">Select Your City</h2>
                  <select
                    id="meeting-city"
                    value={form.city}
                    onChange={(event) => updateForm("city", event.target.value)}
                  >
                    <option value="">Choose city</option>
                    {CITIES.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="meeting-modal-primary"
                    onClick={nextStep}
                    disabled={!form.city}
                  >
                    Continue
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="meeting-modal-step">
                  <h2>Select Date &amp; Time</h2>
                  <div
                    className="meeting-modal-date"
                    onClick={openDatePicker}
                  >
                    <input
                      ref={dateInputRef}
                      id="meeting-date"
                      type="date"
                      min={getTodayValue()}
                      value={form.date}
                      onChange={(event) => {
                        updateForm("date", event.target.value);
                        updateForm("time", "");
                      }}
                    />
                    <FaRegCalendarAlt aria-hidden="true" />
                  </div>
                  <h3>Select Time Slot</h3>
                  {isLoadingSlots && <p className="meeting-modal-error">Checking booked slots...</p>}
                  <div className="meeting-modal-slots" aria-label="Time slots">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot.label}
                        type="button"
                        className={form.time === slot.label ? "is-selected" : ""}
                        disabled={!form.date || slot.disabled}
                        onClick={() => updateForm("time", slot.label)}
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                  <div className="meeting-modal-actions">
                    <button type="button" className="meeting-modal-secondary" onClick={prevStep}>
                      Back
                    </button>
                    <button
                      type="button"
                      className="meeting-modal-primary"
                      onClick={nextStep}
                      disabled={!form.date || !form.time}
                    >
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="meeting-modal-step">
                  <h2>Your Contact Details</h2>
                  <input
                    id="meeting-name"
                    type="text"
                    value={form.name}
                    placeholder="Enter your full name"
                    autoComplete="name"
                    onChange={(event) => {
                      if (/^[A-Za-z ]*$/.test(event.target.value)) {
                        updateForm("name", event.target.value);
                      }
                    }}
                  />
                  <input
                    id="meeting-phone"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.phone}
                    placeholder="Enter your phone number"
                    autoComplete="tel"
                    maxLength={10}
                    onChange={(event) => {
                      const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, 10);
                      updateForm("phone", digitsOnly);
                    }}
                  />
                  <input
                    id="meeting-email"
                    type="email"
                    value={form.email}
                    placeholder="Enter your email address"
                    autoComplete="email"
                    onChange={(event) => updateForm("email", event.target.value)}
                  />
                  <div className="meeting-modal-actions">
                    <button type="button" className="meeting-modal-secondary" onClick={prevStep}>
                      Back
                    </button>
                    <button
                      type="button"
                      className="meeting-modal-primary"
                      onClick={confirmBooking}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Booking..." : "Confirm Booking"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
